import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { numberFormat } from 'legacy/common/utils/number';

import { useEventDashboard } from '../../../context';
import EventDate from './components/EventDate';
import Tag, { TagTypes } from 'legacy/components/Tag';
import DashboardDataCell from './components/DashboardDataCell';
import PayoutDestination from '../../PayoutDestination';
import Button, { ButtonType } from 'legacy/common/components/button/Button';
import { useRequestEarlyPayout } from 'legacy/events/hooks';
import DashboardEarlyPayoutsModal from '../../DashboardEarlyPayoutsModal';
import { EarlyPayoutRequestResult } from 'legacy/events/types';
import * as toast from 'legacy/common/utils/toast';
import ActionRequiredToolTip from 'legacy/components/pages/Profile/User/components/ActionRequiredToolTip';

const getPayoutStatuses = (t: any) => ({
  paid: {
    type: TagTypes.Success,
    label: t('events:dashboard.header.payoutStatuses.paid.label'),
    description: t('events:dashboard.header.payoutStatuses.paid.description')
  },
  pending: {
    type: TagTypes.Neutral,
    label: t('events:dashboard.header.payoutStatuses.pending.label'),
    description: t('events:dashboard.header.payoutStatuses.pending.description')
  },
  in_transit: {
    type: TagTypes.Warning,
    label: t('events:dashboard.header.payoutStatuses.inTransit.label'),
    description: t(
      'events:dashboard.header.payoutStatuses.inTransit.description'
    )
  },
  canceled: {
    type: TagTypes.Danger,
    label: t('events:dashboard.header.payoutStatuses.canceled.label'),
    description: t(
      'events:dashboard.header.payoutStatuses.canceled.description'
    )
  },
  failed: {
    type: TagTypes.Danger,
    label: t('events:dashboard.header.payoutStatuses.failed.label'),
    description: t('events:dashboard.header.payoutStatuses.failed.description')
  },
  default: {
    type: TagTypes.Neutral,
    label: t('events:dashboard.header.payoutStatuses.default.label'),
    description: t('events:dashboard.header.payoutStatuses.default.description')
  }
});

const DashboardHeader = () => {
  const { t } = useTranslation(['events']);
  const [earlyPayoutsModalState, setEarlyPayoutsModalState] = useState<{
    open: boolean;
    result: EarlyPayoutRequestResult | undefined;
    isError: boolean;
  }>({
    open: false,
    result: undefined,
    isError: false
  });
  const {
    data: { event, eventAnalytics }
  } = useEventDashboard();
  const requestEarlyPayout = useRequestEarlyPayout();

  const handleResponse = (result: EarlyPayoutRequestResult) => {
    switch (result) {
      case EarlyPayoutRequestResult.SUCCESS:
        break;
      case EarlyPayoutRequestResult.NO_ORGANIZATION:
      case EarlyPayoutRequestResult.ORG_EARLY_PAYOUTS_UNSET:
      case EarlyPayoutRequestResult.ORG_EARLY_PAYOUTS_REVIEWING:
        setEarlyPayoutsModalState({
          ...earlyPayoutsModalState,
          open: true,
          result
        });
        break;
      case EarlyPayoutRequestResult.NOT_HOST:
      case EarlyPayoutRequestResult.ORG_USER_NOT_MEMBER:
      case EarlyPayoutRequestResult.ORG_EARLY_PAYOUTS_DENIED:
      case EarlyPayoutRequestResult.EVENT_ENDED:
      case EarlyPayoutRequestResult.EVENT_WITH_PROMOTERS:
        setEarlyPayoutsModalState({
          open: true,
          result,
          isError: true
        });
        break;
      default:
        break;
    }
  };

  const handleClearModalState = () =>
    setEarlyPayoutsModalState({
      open: false,
      result: undefined,
      isError: false
    });

  const handleEarlyPayout = () => {
    requestEarlyPayout.mutate(
      { eventId: event._id },
      {
        onSuccess: (data: any) => {
          if (data.data?.result === EarlyPayoutRequestResult.SUCCESS) {
            toast.success(
              t('events:dashboard.content.requestSubmittedSuccess')
            );
          }
          handleResponse(data.data?.result);
        }
      }
    );
  };

  const PAYOUT_STATUSES = getPayoutStatuses(t);
  const payoutStatusData = event?.payoutStatus
    ? PAYOUT_STATUSES[event?.payoutStatus]
    : PAYOUT_STATUSES.default;

  return (
    <>
      <div className="flex justify-between items-start">
        <div className="flex gap-4 flex-[0_0_70%]">
          <div className="h-[70px] w-[56px] rounded-xl overflow-hidden bg-neutral-300">
            {event.photo?.url && (
              <img src={event.photo?.url} className="h-[70px] w-[56px]" />
            )}
          </div>
          <div className="flex flex-1 flex-col justify-center gap-1 md:gap-2">
            <h2 className="text-xl md:text-2xl font-bold">{event.title}</h2>
            <div className="text-gray-500 text-sm font-semibold flex gap-1 align-center">
              {numberFormat(eventAnalytics.tickets.views)}{' '}
              {t('events:dashboard.content.views')}
              <span>•</span>
              <div className="flex items-center gap-1">
                <EventDate
                  activeStatus={event.activeStatus}
                  startDate={event.startDate}
                  endDate={event.endDate}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="flex flex-[0_0_30%] mt-2 flex-col gap-2">
          <div className="flex flex-row gap-2">
            <DashboardDataCell
              label={t('events:dashboard.header.payoutStatus')}
            >
              <div className="flex flex-col items-start gap-2">
                <ActionRequiredToolTip />

                <Tag
                  id="payoutStatus"
                  type={payoutStatusData.type}
                  label={payoutStatusData.label}
                >
                  {payoutStatusData.description}
                </Tag>
              </div>
            </DashboardDataCell>
            {!event.isFree && (
              <DashboardDataCell
                label={t('events:dashboard.header.payoutDestination')}
              >
                <PayoutDestination
                  eventId={event._id}
                  payoutDestination={event.payoutDestination}
                  banks={event.availablePayoutDestinations}
                  disabled={
                    event.userHostStatus !== 'host' ||
                    event.payoutStatus === 'paid'
                  }
                />
              </DashboardDataCell>
            )}
          </div>
          <Button type={ButtonType.Inverted} onClick={handleEarlyPayout}>
            {t('events:dashboard.content.requestEarlyPayout')}
          </Button>
        </div>
      </div>
      <DashboardEarlyPayoutsModal
        open={earlyPayoutsModalState.open}
        onClose={handleClearModalState}
        result={earlyPayoutsModalState.result}
        isError={earlyPayoutsModalState.isError}
      />
    </>
  );
};

export default DashboardHeader;
